import React, { FC, useState } from "react";
import { Card, Form, Button, Input } from "antd";
import styled from "styled-components";
import { Store } from "antd/lib/form/interface";
import { useLostPasswordMutation } from "../../queries/auto-generated/hooks";
import { ViewTypeUpdater } from ".";
import _ from "lodash";

const LostPassword: FC<{ setType: ViewTypeUpdater }> = ({ setType }) => {
  const [form] = Form.useForm();
  const [lostPasswordMutation] = useLostPasswordMutation();
  const [showCompletionMessage, setShowCompletionMessage] = useState<boolean>(
    false
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedAtLeastOnce, setHasSubmittedAtLeastOnce] = useState<
    boolean
  >(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const validateTrigger = hasSubmittedAtLeastOnce
    ? ["onChange", "onSubmit"]
    : "onSubmit";

  const handleButtonClick = () => {
    setIsSubmitting(true);
    setErrorMessages([]);
    setHasSubmittedAtLeastOnce(true);
    form.submit();
  };

  const handleFinish = (values: Store) => {
    const { email } = values;
    lostPasswordMutation({
      variables: {
        email,
      },
    })
      .then(({ data }) => {
        const okay = data?.forgotPassword?.ok ?? false;

        if (okay) {
          setShowCompletionMessage(true);
        } else {
          setErrorMessages([
            "There was a problem sending the password reset email",
          ]);
        }
      })
      .catch((e) => {
        const errorId = _.get(
          e,
          "graphQLErrors[0].extensions.exception.data.message[0].messages[0].id"
        );
        if (errorId === "Auth.form.error.user.not-exist") {
          setShowCompletionMessage(true);
        } else {
          setErrorMessages([
            "There was a problem sending the password reset email",
          ]);
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleFinishedFailed = () => {
    setIsSubmitting(false);
  };

  const ErrorMessages: FC = () => {
    if (!errorMessages || errorMessages.length === 0) {
      return null;
    }

    return (
      <StyledList>
        {errorMessages.map((message) => (
          <li>{message}</li>
        ))}
      </StyledList>
    );
  };

  const primaryAction = (
    <Button
      size="large"
      type="primary"
      shape="round"
      block
      onClick={handleButtonClick}
      loading={isSubmitting}
    >
      Send password reset email
    </Button>
  );

  if (showCompletionMessage) {
    const message = `Please check your email for password reset instructions.`;

    return <StyledMessageCard>{message}</StyledMessageCard>;
  }

  return (
    <Card title="Lost password">
      <Form
        form={form}
        name="lost_password"
        scrollToFirstError
        colon={false}
        hideRequiredMark
        onFinish={handleFinish}
        onFinishFailed={handleFinishedFailed}
        validateTrigger={validateTrigger}
      >
        <StyledItem
          name="email"
          label="Email"
          rules={[
            {
              required: true,
              message: "Please input your email address",
              type: "email",
            },
          ]}
        >
          <Input placeholder="Email" />
        </StyledItem>
        <StyledItem name="extra" hidden>
          <Input />
        </StyledItem>

        <ErrorMessages />
        {primaryAction}
        <AlternativeOptions>
          <Button type="text" shape="round" onClick={() => setType(undefined)}>
            Back
          </Button>
          <Button type="text" shape="round" onClick={() => setType("login")}>
            Login
          </Button>
        </AlternativeOptions>
      </Form>
    </Card>
  );
};

const StyledItem = styled(Form.Item)`
  display: block;
`;

const StyledList = styled.ul`
  list-style: none;
  color: #a61d24;
  padding-left: 0;
`;

const AlternativeOptions = styled.div`
  padding-top: 2em;
  text-align: center;
  display: block;
`;

const StyledMessageCard = styled(Card)`
  text-align: center;
`;

export default LostPassword;
